import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CreditoService, CreditoEstatisticas, CreditoTransacao } from '@/services/creditoService';
import { clientService, Client, ClientStats } from '@/services/clientService';
import reservaService, { Reserva } from '@/services/reservaService';
import { AlertTriangle, RefreshCw, Wallet, CreditCard, BarChart3, Calendar, Clock, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import ResponsiveAdminLayout from '@/components/admin/ResponsiveAdminLayout';
import { ResponsiveMetricCard } from '@/components/ui/responsive-charts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface DiaSemanaStats {
  nome: string;
  contagem: number;
  porcentagem: number;
}

interface BlocoHorarioStats {
  intervalo: string;
  contagem: number;
  porcentagem: number;
}

interface DiaBlocoCombinadoStats {
  diaSemana: string;
  blocoHorario: string;
  contagem: number;
}

const diasSemanaNome = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'] as const;

const AnaliseReservas: React.FC<{ reservas: Reserva[]; isLoading: boolean }> = ({ reservas, isLoading }) => {

  const analiseDiasSemana = React.useMemo<DiaSemanaStats[]>((): DiaSemanaStats[] => {
    if (!reservas || reservas.length === 0) return [];
    const contagemPorDia = new Map<number, number>();
    reservas.forEach((reserva) => {
      const data = new Date(reserva.data);
      const diaSemana = data.getDay();
      contagemPorDia.set(diaSemana, (contagemPorDia.get(diaSemana) || 0) + 1);
    });
    const total = reservas.length;
    const resultado: DiaSemanaStats[] = [];
    contagemPorDia.forEach((contagem, dia) => {
      resultado.push({
        nome: diasSemanaNome[dia],
        contagem,
        porcentagem: (contagem / total) * 100,
      });
    });
    return resultado.sort((a, b) => b.porcentagem - a.porcentagem);
  }, [reservas]);

  const analiseBlocosHorario = React.useMemo<BlocoHorarioStats[]>((): BlocoHorarioStats[] => {
    if (!reservas || reservas.length === 0) return [];
    const contagemPorBloco = new Map<string, number>();
    reservas.forEach((reserva) => {
      const bloco = reserva.bloco;
      const intervalo = `${bloco.hora_inicio} - ${bloco.hora_fim}`;
      contagemPorBloco.set(intervalo, (contagemPorBloco.get(intervalo) || 0) + 1);
    });
    const total = reservas.length;
    const resultado: BlocoHorarioStats[] = [];
    contagemPorBloco.forEach((contagem, intervalo) => {
      resultado.push({
        intervalo,
        contagem,
        porcentagem: (contagem / total) * 100,
      });
    });
    return resultado.sort((a, b) => b.porcentagem - a.porcentagem);
  }, [reservas]);

  const analiseCombinada = React.useMemo<DiaBlocoCombinadoStats[]>((): DiaBlocoCombinadoStats[] => {
    if (!reservas || reservas.length === 0) return [];
    const contagemPorCombinacao = new Map<string, { diaSemana: string; blocoHorario: string; contagem: number }>();
    reservas.forEach((reserva) => {
      const data = new Date(reserva.data);
      const diaSemana = diasSemanaNome[data.getDay()];
      const bloco = reserva.bloco;
      const blocoHorario = `${bloco.hora_inicio} - ${bloco.hora_fim}`;
      const chave = `${diaSemana}|${blocoHorario}`;
      const existente = contagemPorCombinacao.get(chave);
      if (existente) {
        existente.contagem += 1;
      } else {
        contagemPorCombinacao.set(chave, { diaSemana, blocoHorario, contagem: 1 });
      }
    });
    const resultado = Array.from(contagemPorCombinacao.values());
    return resultado.sort((a, b) => b.contagem - a.contagem).slice(0, 10);
  }, [reservas]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Análise de Utilização (12 meses)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Análise de Utilização (12 meses)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4" />
            <h3 className="font-semibold">Dias da Semana</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dia</TableHead>
                <TableHead className="text-right">Contagem</TableHead>
                <TableHead className="text-right">Porcentagem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analiseDiasSemana.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground">
                    Nenhum dado disponível
                  </TableCell>
                </TableRow>
              )}
              {analiseDiasSemana.map((item, index) => (
                <TableRow key={`${item.nome}-${index}`}>
                  <TableCell>{item.nome}</TableCell>
                  <TableCell className="text-right">{item.contagem}</TableCell>
                  <TableCell className="text-right">{new Intl.NumberFormat('pt-BR', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(item.porcentagem / 100)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4" />
            <h3 className="font-semibold">Blocos de Horário</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Intervalo</TableHead>
                <TableHead className="text-right">Contagem</TableHead>
                <TableHead className="text-right">Porcentagem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analiseBlocosHorario.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground">
                    Nenhum dado disponível
                  </TableCell>
                </TableRow>
              )}
              {analiseBlocosHorario.map((item, index) => (
                <TableRow key={`${item.intervalo}-${index}`}>
                  <TableCell>{item.intervalo}</TableCell>
                  <TableCell className="text-right">{item.contagem}</TableCell>
                  <TableCell className="text-right">{new Intl.NumberFormat('pt-BR', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(item.porcentagem / 100)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4" />
            <h3 className="font-semibold">Top 10 Combinações (Dia e Horário)</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dia</TableHead>
                <TableHead>Horário</TableHead>
                <TableHead className="text-right">Contagem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analiseCombinada.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground">
                    Nenhum dado disponível
                  </TableCell>
                </TableRow>
              )}
              {analiseCombinada.map((item, index) => (
                <TableRow key={`${item.diaSemana}-${item.blocoHorario}-${index}`}>
                  <TableCell>{item.diaSemana}</TableCell>
                  <TableCell>{item.blocoHorario}</TableCell>
                  <TableCell className="text-right">{item.contagem}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

// ===================================
// Componente CreditCards
// ===================================
const CreditCards: React.FC<{ estatisticas?: CreditoEstatisticas; isLoading: boolean; totalPix?: number; totalStripe?: number }> = ({ estatisticas, isLoading, totalPix, totalStripe }) => {
  const metrics = estatisticas ? [
    {
      title: "Total de Creditos Recebidos (Receita)",
      value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((totalStripe || 0) + (totalPix || 0)),
      change: "efetivamente utilizados no período total",
      bg: "bg-gradient-to-r from-purple-500 to-pink-500",
      icon: BarChart3
    },
    {
      title: "Créditos em Reservas",
      value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(estatisticas.creditosReservados || 0),
      change: "utilizados em reservas",
      bg: "bg-gradient-to-r from-orange-500 to-red-500",
      icon: CreditCard
    },
    {
      title: "Total de Creditos não utilizados em reservas",
      value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(estatisticas.totalCreditos),
      change: "saldo disponível dos clientes",
      bg: "bg-gradient-to-r from-blue-500 to-cyan-500",
      icon: Wallet
    }
  ] : [];

  if (isLoading) {
    return (
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-6 items-stretch">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-[140px] w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-6 items-stretch">
      {metrics.map((metric, index) => (
        <ResponsiveMetricCard
          key={index}
          {...metric}
        />
      ))}
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { data: estatisticas, isLoading: isLoadingCredits, error: creditosError, refetch } = useQuery<CreditoEstatisticas>({
    queryKey: ['creditoEstatisticas'],
    queryFn: () => CreditoService.getEstatisticas(),
  });

  const { data: clientStats, isLoading: isLoadingClientStats, error: clientStatsError } = useQuery<ClientStats>({
    queryKey: ['clientStatsDashboard'],
    queryFn: () => clientService.getClientStats(),
  });

  const formatDate = React.useCallback((date: Date): string => date.toISOString().split('T')[0], []);
  const inicioReceitas = React.useMemo(() => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - 11);
    date.setHours(0, 0, 0, 0);
    return formatDate(date);
  }, [formatDate]);

  const loadAllReservas = React.useCallback(async (filters: { status?: string; user_id?: string; data_inicio?: string; data_fim?: string } = {}) => {
    const limit = 200;
    let page = 1;
    let hasNext = true;
    const todasReservas: Reserva[] = [];
    while (hasNext) {
      const response = await reservaService.findAll({ ...filters, page, limit });
      todasReservas.push(...response.data);
      hasNext = response.pagination.hasNext || page < response.pagination.totalPages;
      page += 1;
    }
    return todasReservas;
  }, []);

  const loadAllClientes = React.useCallback(async () => {
    const limit = 200;
    let page = 1;
    let hasNext = true;
    const todosClientes: Client[] = [];
    while (hasNext) {
      const response = await clientService.listClients({ page, limit });
      todosClientes.push(...response.data);
      hasNext = response.pagination.hasNext;
      page += 1;
    }
    return todosClientes;
  }, []);

  const { data: reservasRecebidas, isLoading: isLoadingReservasRecebidas, error: reservasRecebidasError } = useQuery<Reserva[]>({
    queryKey: ['reservasRecebidas12m', inicioReceitas],
    queryFn: () => loadAllReservas({ status: 'USADO', data_inicio: inicioReceitas }),
  });

  const { data: reservasTotais, isLoading: isLoadingReservasTotais, error: reservasTotaisError } = useQuery<Reserva[]>({
    queryKey: ['reservasTotaisClientes'],
    queryFn: () => loadAllReservas({ status: 'USADO' }),
  });

  const { data: clientes, isLoading: isLoadingClientes, error: clientesError } = useQuery<Client[]>({
    queryKey: ['clientesDashboardLista'],
    queryFn: () => loadAllClientes(),
  });

  const loadAllTransacoes = React.useCallback(async () => {
    const limit = 200;
    let page = 1;
    let hasNext = true;
    const todasTransacoes: CreditoTransacao[] = [];
    while (hasNext) {
      const response = await CreditoService.getAllTransacoes(page, limit);
      todasTransacoes.push(...response.transacoes);
      hasNext = response.page < response.totalPages;
      page += 1;
    }
    return todasTransacoes;
  }, []);

  const { data: transacoes, isLoading: isLoadingTransacoes, error: transacoesError } = useQuery<CreditoTransacao[]>({
    queryKey: ['transacoesDashboard'],
    queryFn: () => loadAllTransacoes(),
  });

  const receitasMensaisRows = React.useMemo(() => {
    const mapStripe = new Map<string, number>();
    transacoes?.forEach((transacao) => {
      if (transacao.tipo === 'COMPRA') {
        const data = new Date(transacao.created_at);
        if (!Number.isNaN(data.getTime())) {
          const key = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
          mapStripe.set(key, (mapStripe.get(key) || 0) + transacao.valor);
        }
      }
    });

    const mapPix = new Map<string, number>();
    transacoes?.forEach((transacao) => {
      if (transacao.tipo === 'AJUSTE' && transacao.valor > 0) {
        const data = new Date(transacao.created_at);
        const key = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
        mapPix.set(key, (mapPix.get(key) || 0) + transacao.valor);
      }
    });

    const rows: Array<{ ano: number; mes: string; valorStripe: number; valorPix: number; valorTotal: number }> = [];
    for (let i = 0; i < 12; i += 1) {
      const date = new Date();
      date.setDate(1);
      date.setMonth(date.getMonth() - i);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const mes = new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(date);
      rows.push({
        ano: date.getFullYear(),
        mes,
        valorStripe: mapStripe.get(key) || 0,
        valorPix: mapPix.get(key) || 0,
        valorTotal: (mapStripe.get(key) || 0) + (mapPix.get(key) || 0),
      });
    }
    return rows;
  }, [transacoes]);

  const totalPix = React.useMemo(() => {
    return transacoes?.reduce((total, transacao) => {
      if (transacao.tipo === 'AJUSTE' && transacao.valor > 0) {
        return total + transacao.valor;
      }
      return total;
    }, 0) || 0;
  }, [transacoes]);

  const totalStripe = React.useMemo(() => {
    return transacoes?.reduce((total, transacao) => {
      if (transacao.tipo === 'COMPRA') {
        return total + transacao.valor;
      }
      return total;
    }, 0) || 0;
  }, [transacoes]);

  const totalPagoPorCliente = React.useMemo(() => {
    const mapStripe = new Map<string, number>();
    transacoes?.forEach((transacao) => {
      if (transacao.tipo === 'COMPRA') {
        mapStripe.set(transacao.user_id, (mapStripe.get(transacao.user_id) || 0) + transacao.valor);
      }
    });

    const mapPix = new Map<string, number>();
    transacoes?.forEach((transacao) => {
      if (transacao.tipo === 'AJUSTE' && transacao.valor > 0) {
        mapPix.set(transacao.user_id, (mapPix.get(transacao.user_id) || 0) + transacao.valor);
      }
    });

    const nomes = new Map<string, string>();
    clientes?.forEach((cliente) => {
      nomes.set(cliente.id, cliente.nome_completo);
    });

    const allUserIds = new Set([...mapStripe.keys(), ...mapPix.keys()]);

    return Array.from(allUserIds)
      .map((userId) => {
        const totalStripe = mapStripe.get(userId) || 0;
        const totalPix = mapPix.get(userId) || 0;
        return {
          nome: nomes.get(userId) || 'Cliente',
          totalStripe,
          totalPix,
          totalGeral: totalStripe + totalPix,
        };
      })
      .sort((a, b) => b.totalGeral - a.totalGeral);
  }, [clientes, transacoes]);

  const renderContent = () => {
    if (creditosError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <AlertTriangle className="w-12 h-12 text-destructive" />
          <p className="text-muted-foreground">Erro ao carregar dados do dashboard.</p>
          <p className="text-sm text-muted-foreground">{(creditosError as Error).message}</p>
          <Button onClick={() => refetch()} className="mt-4">
            <RefreshCw className="w-4 h-4 mr-2" />
            Tentar Novamente
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <CreditCards estatisticas={estatisticas} isLoading={isLoadingCredits} totalPix={totalPix} totalStripe={totalStripe} />
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-stretch">
          <Card>
            <CardHeader>
            <CardTitle>Receita mensal dos últimos 12 meses</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ano</TableHead>
                    <TableHead>Mês</TableHead>
                    <TableHead className="text-right">Valor Total (STRIPE)</TableHead>
                    <TableHead className="text-right">Valor Total (PIX)</TableHead>
                    <TableHead className="text-right">Valor Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(isLoadingReservasRecebidas || isLoadingTransacoes) && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  )}
                  {(reservasRecebidasError || transacoesError) && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        Erro ao carregar dados
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoadingReservasRecebidas && !isLoadingTransacoes && !reservasRecebidasError && !transacoesError && receitasMensaisRows.map((row, index) => (
                    <TableRow key={`${row.ano}-${row.mes}-${index}`}>
                      <TableCell>{row.ano}</TableCell>
                      <TableCell className="capitalize">{row.mes}</TableCell>
                      <TableCell className="text-right">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.valorStripe)}
                      </TableCell>
                      <TableCell className="text-right">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.valorPix)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.valorTotal)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Total Gasto por cliente</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="text-right">Valor Total (STRIPE)</TableHead>
                    <TableHead className="text-right">Valor Total (PIX)</TableHead>
                    <TableHead className="text-right">Valor Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(isLoadingReservasTotais || isLoadingClientes || isLoadingTransacoes) && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  )}
                  {(reservasTotaisError || clientesError || transacoesError) && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        Erro ao carregar dados
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoadingReservasTotais && !isLoadingClientes && !isLoadingTransacoes && !reservasTotaisError && !clientesError && !transacoesError && totalPagoPorCliente.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        Nenhum pagamento encontrado
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoadingReservasTotais && !isLoadingClientes && !isLoadingTransacoes && !reservasTotaisError && !clientesError && !transacoesError && totalPagoPorCliente.map((row, index) => (
                    <TableRow key={`${row.nome}-${index}`}>
                      <TableCell>{row.nome}</TableCell>
                      <TableCell className="text-right">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.totalStripe)}
                      </TableCell>
                      <TableCell className="text-right">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.totalPix)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.totalGeral)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <AnaliseReservas reservas={reservasRecebidas || []} isLoading={isLoadingReservasRecebidas} />
        </div>
      </div>
    );
  };

  return (
    <ResponsiveAdminLayout
      title="Dashboard"
      subtitle="Visão geral do sistema VITASANA"
    >
      {renderContent()}
    </ResponsiveAdminLayout>
  );
};

export default Dashboard;
